import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { chatService } from '../../data/dataService';
import { baseParishId } from '../../utils/scopeResolution';
import { supabaseFetch } from '../../lib/supabase';
import { useTheme } from '../../themes/ThemeProvider';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsForm from '../core/HeadersAndFooters/FooterButtonsForm';
import ConfirmationModal from '../core/ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { addBreadcrumb, addCaughtError } from '../../utils/errorCapture';
import { isRateLimited, rateLimitMessage, RATE_LIMIT_TITLE_KEY } from '../../utils/rateLimitMessage';

const AddEditChat = ({ userId, onBack, currentContext, mode = 'add', chat = null, preselectedGroupId = null, selectedArea = null, visitBoundaryId = null }) => {
  const { t } = useTranslation('chat');
  const theme = useTheme();
  const { isMobile } = useMediaQuery();
  const [formData, setFormData] = useState({
    headline: mode === 'edit' && chat ? chat.headline : '',
    content: mode === 'edit' && chat ? chat.content : ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });

  // Locality mode: publishing directly to the user's locality with no group required.
  // Mirrors the isLocalityMode pattern from AddEditArticle/AddEditEvent.
  const isLocalityMode = !preselectedGroupId && !chat?.community_id &&
    (currentContext === 'locality' || currentContext?.id === 'locality' || currentContext?.type === 'location');

  // home_locality_id resolution for locality chats
  const [homeLocalityId, setHomeLocalityId] = useState(null);
  const [homeLocalityLoading, setHomeLocalityLoading] = useState(isLocalityMode);

  // Resolve home_locality_id from the browsed area (simplified vs articles — no picker needed)
  useEffect(() => {
    if (!isLocalityMode) {
      setHomeLocalityLoading(false);
      return;
    }

    if (mode === 'edit' && chat?.home_locality_id) {
      setHomeLocalityId(chat.home_locality_id);
      setHomeLocalityLoading(false);
      return;
    }

    const resolveHomeLocality = async () => {
      setHomeLocalityLoading(true);

      // Precedence 0: an active visit (Change 3) — chat posted while visiting targets the
      // visited locality, never home (addendum-1 §A/§C). Takes priority over selectedArea/home,
      // but only for new chats — an edit (guarded above) never reaches this branch.
      if (visitBoundaryId) {
        setHomeLocalityId(Number(visitBoundaryId));
        setHomeLocalityLoading(false);
        return;
      }

      // Precedence 1–2: the member's own parish in the area being browsed (the shared rule:
      // base parish, else the first saved boundary).
      const areaParish = baseParishId(selectedArea);
      if (areaParish != null) {
        setHomeLocalityId(areaParish);
        setHomeLocalityLoading(false);
        return;
      }

      // Precedence 3: User's home area from database
      if (userId) {
        try {
          const { data: homeArea } = await supabaseFetch.query('user_areas', {
            select: 'primary_boundary_id,boundary_ids',
            eq: { user_id: userId, is_home: true },
            single: true
          });
          const homeParish = baseParishId(homeArea);
          if (homeParish != null) {
            setHomeLocalityId(homeParish);
            setHomeLocalityLoading(false);
            return;
          }
        } catch (err) {
          console.warn('Chat locality resolution failed:', err);
        }
      }

      // No locality resolved
      setHomeLocalityId(null);
      setHomeLocalityLoading(false);
    };

    resolveHomeLocality();
  }, [isLocalityMode, mode, chat, selectedArea, userId, visitBoundaryId]);

  const pageTitle = mode === 'edit' ? t('edit_chat') : t('new_chat');
  const headerContext = { ...currentContext, name: pageTitle };

  // #78: "unsaved changes" means the headline or message changed since the form opened.
  const { confirmLeave, markSaved, dialog: leaveDialog } = useUnsavedChanges({
    value: formData,
    onLeave: onBack,
  });

  // Logo click listener - navigate back to MainFeed; asks first when something changed (#78).
  useEffect(() => {
    const handleLogoClick = async () => {
      if ((await confirmLeave()) && onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack, confirmLeave]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.headline || formData.headline.trim().length < 3) {
      newErrors.headline = t('error_headline_too_short');
    }
    if (!formData.content || formData.content.trim().length < 10) {
      newErrors.content = t('error_content_too_short');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    addBreadcrumb('form-submit', { action: 'chat-create', component: 'AddEditChat' });
    if (!validateForm()) return;

    // Locality mode: require resolved home_locality_id
    if (isLocalityMode) {
      if (homeLocalityLoading) return; // Still resolving
      if (!homeLocalityId) {
        setModal({
          visible: true,
          message: t('error_no_locality_resolved'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
        return;
      }
    } else if (!preselectedGroupId) {
      // Group mode: require a group
      setModal({
        visible: true,
        message: t('error_no_group_selected'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    setLoading(true);
    try {
      await chatService.createOriginalChat({
        // Saved exactly as typed (#93): safety is at display.
        headline: formData.headline.trim(),
        content: formData.content.trim(),
        community_id: isLocalityMode ? null : preselectedGroupId,
        home_locality_id: isLocalityMode ? homeLocalityId : null,
        author_id: userId
      });
      markSaved();
      setModal({
        visible: true,
        message: mode === 'edit' ? t('success_chat_updated') : t('success_chat_created'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          onBack();
        }
      });
    } catch (error) {
      addCaughtError(error.message || String(error), 'AddEditChat:save');
      const isSessionExpired = error?.code === 'SESSION_EXPIRED' || error?.message === 'SESSION_EXPIRED';
      // 42501: refused where the writer cannot read (e.g. not a member) — one neutral message,
      // as ConcertinaThread's surfaceMutationError.
      const isRefused = error?.code === '42501' || /"code"\s*:\s*"42501"/.test(error?.message || '');
      const chatErrorMessage = isSessionExpired
        ? t('chat:session_expired')
        : isRefused
        ? t('chat:not_able_to_post_here')
        : isRateLimited(error)
        ? rateLimitMessage(error, t)
        : t('error_failed_to_save', { error: error.message });
      setModal({
        visible: true,
        title: isRateLimited(error) ? t(RATE_LIMIT_TITLE_KEY) : undefined,
        message: chatErrorMessage,
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          if (isSessionExpired) {
            window.dispatchEvent(new CustomEvent('ournet:showAuthRequired', {
              detail: { message: chatErrorMessage }
            }));
          }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#EDF2F8', display: 'flex', flexDirection: 'column' }}>
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={headerContext}
        onBack={onBack}
        headerTitle={mode === 'edit' ? t('edit_chat') : t('new_chat')}
        onCheckUnsaved={confirmLeave}
      />
      <div style={{ flex: 1, marginTop: '80px', marginBottom: isMobile ? '80px' : '120px', padding: isMobile ? '16px' : '40px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', background: 'white', borderRadius: '20px', padding: isMobile ? '20px' : '40px' }}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>{t('field_headline')} *</label>
            <input
              type="text"
              value={formData.headline}
              onChange={(e) => handleChange('headline', e.target.value)}
              placeholder={t('placeholder_headline')}
              style={{ width: '100%', padding: '12px', fontSize: '16px', border: errors.headline ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '8px' }}
            />
            {errors.headline && <span style={{ fontSize: '12px', color: '#EF4444' }}>{errors.headline}</span>}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>{t('field_content')} *</label>
            <textarea
              value={formData.content}
              onChange={(e) => handleChange('content', e.target.value)}
              placeholder={t('placeholder_content')}
              rows={8}
              style={{ width: '100%', padding: '12px', fontSize: '16px', border: errors.content ? '2px solid #EF4444' : '1px solid #D1D5DB', borderRadius: '8px', resize: 'vertical' }}
            />
            {errors.content && <span style={{ fontSize: '12px', color: '#EF4444' }}>{errors.content}</span>}
          </div>
        </div>
      </div>
      <FooterContainer viewType="form" isMobile={isMobile}>
        <FooterButtonsForm onCancel={async () => { if (await confirmLeave()) onBack(); }} onCreateAndExit={handleSave} isEditMode={mode === 'edit'} />
      </FooterContainer>

      {leaveDialog}

      <ConfirmationModal
        visible={modal.visible}
        title={modal.title || t('confirmation')}
        message={modal.message}
        onOk={modal.onOk}
      />
    </div>
  );
};

export default AddEditChat;
